import { useState } from 'react';
import { Badge } from '../ui/badge';
import { COUNTRIES } from '../../lib/constants';
import {
  Zap, Flame, Droplets, Trash2, Truck, FlaskConical, Package, Boxes, Globe2, Building2,
} from 'lucide-react';

// Full class strings so Tailwind's JIT keeps them.
const STAGE_TONES = [
  'bg-emerald-50 text-emerald-800 border-emerald-200',
  'bg-sky-50 text-sky-800 border-sky-200',
  'bg-amber-50 text-amber-800 border-amber-200',
  'bg-violet-50 text-violet-800 border-violet-200',
  'bg-rose-50 text-rose-800 border-rose-200',
  'bg-teal-50 text-teal-800 border-teal-200',
  'bg-lime-50 text-lime-800 border-lime-200',
  'bg-orange-50 text-orange-800 border-orange-200',
  'bg-cyan-50 text-cyan-800 border-cyan-200',
  'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200',
];

const STAGE_DOTS = [
  'bg-emerald-500', 'bg-sky-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500',
  'bg-teal-500', 'bg-lime-500', 'bg-orange-500', 'bg-cyan-500', 'bg-fuchsia-500',
];

const stageIndex = (stage) => {
  let h = 0;
  for (const ch of stage || '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % STAGE_TONES.length;
};

export const stageDotClass = (stage) => STAGE_DOTS[stageIndex(stage)];

export const StageBadge = ({ stage, className = '' }) => {
  if (!stage) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant="outline" className={`${STAGE_TONES[stageIndex(stage)]} font-medium whitespace-nowrap ${className}`}>
      {stage}
    </Badge>
  );
};

export const isBlank = (v) => v === null || v === undefined || v === '';
export const isClientProfile = (p) => !isBlank(p?.greenstitch_id) && p.greenstitch_id !== '*';

// '*' means "applies to any value" in the profile lookup; blank means not set.
export const AttrValue = ({ value }) => {
  if (isBlank(value)) return <span className="text-muted-foreground">—</span>;
  if (value === '*') {
    return <Badge variant="outline" className="font-normal text-muted-foreground border-dashed">Any</Badge>;
  }
  return <span>{String(value)}</span>;
};

export const ScopeBadge = ({ profile }) => (
  isClientProfile(profile) ? (
    <Badge variant="outline" className="gap-1 bg-amber-50 text-amber-800 border-amber-200 font-normal whitespace-nowrap">
      <Building2 className="w-3 h-3" />
      <span className="font-mono text-[11px]">{profile.greenstitch_id}</span>
    </Badge>
  ) : (
    <Badge variant="outline" className="gap-1 bg-emerald-50 text-emerald-800 border-emerald-200 font-normal whitespace-nowrap">
      <Globe2 className="w-3 h-3" />
      Secondary
    </Badge>
  )
);

// Order matters: the activity set's own path is checked before its broad classification,
// so "Stationary Combustion › Natural Gas" is a fuel even though it is classed as Energy.
const CATEGORY_STYLES = [
  { kind: 'Water', match: /water/, icon: Droplets, tone: 'bg-sky-100 text-sky-700', bar: 'bg-sky-500' },
  { kind: 'Fuels', match: /combust|fuel|natural gas|diesel|coal|petrol|lpg|feedstock|biomass/, icon: Flame, tone: 'bg-orange-100 text-orange-700', bar: 'bg-orange-500' },
  { kind: 'Electricity & heat', match: /electric|purchased energy|power|steam|heat|voltage/, icon: Zap, tone: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500' },
  { kind: 'Chemicals', match: /chemical|dye|auxiliar|reagent/, icon: FlaskConical, tone: 'bg-violet-100 text-violet-700', bar: 'bg-violet-500' },
  { kind: 'Waste', match: /waste|disposal|landfill|recycl|effluent/, icon: Trash2, tone: 'bg-rose-100 text-rose-700', bar: 'bg-rose-500' },
  { kind: 'Transport', match: /transport|freight|distribution|logistic/, icon: Truck, tone: 'bg-indigo-100 text-indigo-700', bar: 'bg-indigo-500' },
  { kind: 'Materials', match: /purchased|goods|fibre|fiber|material|yarn|fabric/, icon: Package, tone: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500' },
];

const FALLBACK_STYLE = { kind: 'Other', icon: Boxes, tone: 'bg-gray-100 text-gray-700', bar: 'bg-gray-400' };

export const categoryStyle = (row) => {
  const path = [row.category, row.sub_category, row.type, row.sub_type].filter(Boolean).join(' ').toLowerCase();
  const cls = (row.activity_classification || '').toLowerCase();
  return CATEGORY_STYLES.find(s => s.match.test(path))
    || CATEGORY_STYLES.find(s => s.match.test(cls))
    || FALLBACK_STYLE;
};


export const formatQuantity = (value) => {
  if (isBlank(value)) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  if (num !== 0 && Math.abs(num) < 0.001) return num.toExponential(2);
  return num.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

// Profiles store either a country name ("India") or an ISO code ("US").
const COUNTRY_ALIASES = {
  usa: 'US', 'united states of america': 'US', america: 'US',
  uk: 'GB', 'great britain': 'GB', england: 'GB',
  uae: 'AE', korea: 'KR', 'republic of korea': 'KR', vietnam: 'VN', 'viet nam': 'VN',
};

export const resolveCountry = (value) => {
  if (isBlank(value) || value === '*') return null;
  const raw = String(value).trim();
  const lower = raw.toLowerCase();
  const byCode = COUNTRIES.find(c => c.code !== '*' && c.code.toLowerCase() === lower);
  if (byCode) return byCode;
  const byName = COUNTRIES.find(c => c.name.toLowerCase() === lower);
  if (byName) return byName;
  const alias = COUNTRY_ALIASES[lower];
  if (alias) return COUNTRIES.find(c => c.code === alias) || { code: alias, name: raw };
  return { code: null, name: raw };
};

export const CountryFlag = ({ code, className = '' }) => {
  const [failed, setFailed] = useState(false);
  if (!code || failed) {
    return <Globe2 className={`w-4 h-4 text-muted-foreground flex-shrink-0 ${className}`} />;
  }
  return (
    <img
      src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={`w-5 h-[15px] rounded-[2px] object-cover ring-1 ring-black/10 flex-shrink-0 ${className}`}
    />
  );
};

// Flag + full country name; '*' renders as "Any country".
export const CountryLabel = ({ value, sub }) => {
  if (isBlank(value)) return <span className="text-muted-foreground">—</span>;
  if (value === '*') {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
        <Globe2 className="w-4 h-4" /> Any country
      </span>
    );
  }
  const country = resolveCountry(value);
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap" title={country.code ? `${country.name} (${country.code})` : country.name}>
      <CountryFlag code={country.code} />
      <span>{country.name}</span>
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </span>
  );
};
